import { type MouseEvent, memo, useCallback } from 'react';

import { cn, tailwindVariants } from '@air/utils-tailwind';

import {
  getWorkspaceAgentScaleOrCropWindow,
  WORKSPACE_AGENT_CROP_ORIGIN_LONG_SIDE,
  WORKSPACE_AGENT_CROP_ORIGIN_SHORT_SIDE,
  WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS,
  type WorkspaceAgentScaleOrCropOrigin,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';
import { type WorkspaceAgentSmartResizeSize } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

const cropOriginCell = tailwindVariants({
  variants: {
    origin: {
      'bottom-center': 'items-end justify-center',
      'bottom-left': 'items-end justify-start',
      'bottom-right': 'items-end justify-end',
      center: 'items-center justify-center',
      'middle-left': 'items-center justify-start',
      'middle-right': 'items-center justify-end',
      'top-center': 'items-start justify-center',
      'top-left': 'items-start justify-start',
      'top-right': 'items-start justify-end',
    },
  },
});

const cropOriginWindowOrientation = (height: number, width: number): 'landscape' | 'portrait' | 'square' => {
  if (height === width) {
    return 'square';
  }

  if (height > width) {
    return 'portrait';
  }

  return 'landscape';
};

export type WorkspaceAgentGuidedVariantCropOriginFieldProps = {
  cropSize: WorkspaceAgentSmartResizeSize;
  onOriginChange: (origin: WorkspaceAgentScaleOrCropOrigin) => void;
  origin: WorkspaceAgentScaleOrCropOrigin;
};

export const WorkspaceAgentGuidedVariantCropOriginField = memo(
  ({ cropSize, onOriginChange, origin }: WorkspaceAgentGuidedVariantCropOriginFieldProps) => {
    const windowRect = getWorkspaceAgentScaleOrCropWindow({
      frameHeight: WORKSPACE_AGENT_CROP_ORIGIN_SHORT_SIDE,
      frameWidth: WORKSPACE_AGENT_CROP_ORIGIN_LONG_SIDE,
      origin,
      size: cropSize,
    });

    const handleOriginClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        const nextOrigin = WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS.find(
          (option) => option.id === event.currentTarget.value,
        );

        if (nextOrigin) {
          onOriginChange(nextOrigin.id);
        }
      },
      [onOriginChange],
    );

    return (
      <fieldset
        className="m-0 flex shrink-0 flex-col gap-1.5 border-0 p-0"
        data-frame-orientation="landscape"
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CROP_ORIGIN"
      >
        <legend className="float-none flex w-full items-center gap-0.5 px-0 font-medium ads:text-primary text-12 text-gray-12 leading-4">
          Crop starts from
        </legend>
        <div
          className="relative overflow-hidden rounded-2 ads:bg-subtle bg-gray-3"
          style={{ height: WORKSPACE_AGENT_CROP_ORIGIN_SHORT_SIDE, width: WORKSPACE_AGENT_CROP_ORIGIN_LONG_SIDE }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute rounded-2 ads:border-ads-gray-8 border-[1.5px] border-gray-8 transition-[left,top,width,height] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CROP_ORIGIN_WINDOW"
            data-window-orientation={cropOriginWindowOrientation(windowRect.height, windowRect.width)}
            style={{
              height: windowRect.height,
              left: windowRect.left,
              top: windowRect.top,
              width: windowRect.width,
            }}
          />
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
            {WORKSPACE_AGENT_SCALE_OR_CROP_ORIGINS.map((option) => {
              const isSelected = option.id === origin;

              return (
                <button
                  aria-label={option.label}
                  aria-pressed={isSelected}
                  className={cn(
                    'group/cell flex p-1 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9 focus-visible:ring-inset',
                    cropOriginCell({ origin: option.id }),
                  )}
                  data-origin-id={option.id}
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_CROP_ORIGIN_POINT"
                  key={option.id}
                  onClick={handleOriginClick}
                  type="button"
                  value={option.id}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'rounded-full',
                      isSelected
                        ? 'size-2.5 ads:bg-primary bg-altitude-9 ring-2 ring-gray-1'
                        : 'size-1.5 ads:bg-ads-gray-7 bg-gray-7 transition-all group-hover/cell:size-2 ads:group-hover/cell:bg-ads-gray-9 group-hover/cell:bg-gray-9',
                    )}
                    data-selected={isSelected ? 'true' : 'false'}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </fieldset>
    );
  },
);

WorkspaceAgentGuidedVariantCropOriginField.displayName = 'WorkspaceAgentGuidedVariantCropOriginField';
